import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

// Pop up bisa ditutup (tanda silang atau "Nanti"), tapi muncul lagi setelah jeda ini
// selama masih ada TTD surat jalan yang ditolak.
const SNOOZE_MS = 60 * 60 * 1000;
// Cek ulang jeda secara lokal (tanpa request ke server).
const LOCAL_CHECK_MS = 30 * 1000;

const snoozeKey = (userId) => `vaxa:ttd_rejected_snooze:${userId}`;

// Penanda satu penolakan: id pengiriman + waktu ditolak. Pengiriman yang sama bisa
// ditolak berulang kali, jadi penolakan ulang dihitung sebagai penolakan baru.
const sigOf = (it) => `${it.request_id}@${it.rejected_at || ''}`;

function readSnooze(userId) {
  try {
    const raw = localStorage.getItem(snoozeKey(userId));
    if (!raw) return { at: 0, ids: [] };
    const parsed = JSON.parse(raw);
    return { at: Number(parsed?.at) || 0, ids: Array.isArray(parsed?.ids) ? parsed.ids : [] };
  } catch (_e) {
    return { at: 0, ids: [] };
  }
}

function writeSnooze(userId, ids) {
  try {
    localStorage.setItem(snoozeKey(userId), JSON.stringify({ at: Date.now(), ids }));
  } catch (_e) {
    /* storage tidak tersedia: abaikan, jeda tetap jalan lewat memori */
  }
}

function formatWib(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('id-ID', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Jakarta',
  }) + ' WIB';
}

/**
 * Pop up pengingat TTD surat jalan ditolak gudang (Okt 2026).
 *
 * `items` berasal dari polling layout nakes (pending-confirmation.ttd_rejected),
 * jadi tidak ada request tambahan. Pop up:
 *  - muncul di halaman mana pun di area nakes selama ada TTD ditolak,
 *  - bisa ditutup, muncul lagi 1 jam kemudian,
 *  - langsung muncul lagi kalau ada penolakan BARU yang belum pernah dilihat,
 *  - hilang sendiri begitu daftar kosong (nakes sudah upload ulang atau setuju digital).
 */
export default function NakesTtdRejectedPopup({ items = [] }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userId = user?.user_id;
  const [open, setOpen] = useState(false);

  const itemsRef = useRef(items);
  const openRef = useRef(false);
  const closedRef = useRef({ at: 0, ids: [] }); // cadangan di memori kalau localStorage gagal

  useEffect(() => { itemsRef.current = items; }, [items]);
  useEffect(() => { openRef.current = open; }, [open]);

  const maybeOpen = useCallback(() => {
    const list = itemsRef.current || [];
    if (!userId || list.length === 0 || openRef.current) return;
    const stored = readSnooze(userId);
    const mem = closedRef.current;
    const lastClosed = Math.max(stored.at, mem.at);
    const seenSigs = new Set([...(stored.ids || []), ...(mem.ids || [])]);
    const hasNew = list.some((it) => !seenSigs.has(sigOf(it)));
    // Math.abs: kalau jam perangkat diubah mundur, jeda tidak menggantung selamanya.
    if (!hasNew && Math.abs(Date.now() - lastClosed) < SNOOZE_MS) return;
    setOpen(true);
  }, [userId]);

  // Daftar berubah (polling layout): tutup kalau kosong, buka kalau ada yang baru.
  useEffect(() => {
    if (!items || items.length === 0) {
      setOpen(false);
      return;
    }
    maybeOpen();
  }, [items, maybeOpen]);

  // Pemeriksaan lokal berkala supaya jeda 1 jam terpenuhi walau daftar tidak berubah.
  useEffect(() => {
    const t = setInterval(maybeOpen, LOCAL_CHECK_MS);
    return () => clearInterval(t);
  }, [maybeOpen]);

  const handleClose = () => {
    const ids = (itemsRef.current || []).map(sigOf);
    closedRef.current = { at: Date.now(), ids };
    if (userId) writeSnooze(userId, ids);
    setOpen(false);
  };

  const handleGo = (requestId) => {
    // Jeda 1 jam tetap dicatat: nakes sedang mengerjakan, jangan diganggu lagi.
    // Kalau sampai 1 jam belum selesai, pop up kembali.
    handleClose();
    navigate(`/nakes/supply-requests/${requestId}`);
  };

  if (!items || items.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-md" data-testid="ttd-rejected-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            TTD Surat Jalan Ditolak
          </DialogTitle>
          <DialogDescription>
            Gudang menolak tanda tangan di surat jalan kamu. Mohon tanda tangan ulang lalu unggah lagi.
            Kalau surat jalan hilang, buka detail pengiriman dan pakai persetujuan digital.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
          {items.map((it) => (
            <div key={it.request_id} className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm font-semibold text-gray-900">{it.request_id}</p>
              {it.rejected_at && (
                <p className="text-xs text-gray-500">Ditolak {formatWib(it.rejected_at)}</p>
              )}
              {it.reason && (
                <p className="mt-1 text-sm text-gray-700">Alasan: {it.reason}</p>
              )}
              <Button
                size="sm"
                className="mt-2 w-full"
                onClick={() => handleGo(it.request_id)}
                data-testid={`ttd-rejected-go-${it.request_id}`}
              >
                Tanda Tangan Ulang Sekarang
              </Button>
            </div>
          ))}
        </div>

        <Button variant="outline" className="w-full" onClick={handleClose} data-testid="ttd-rejected-later">
          Nanti
        </Button>
      </DialogContent>
    </Dialog>
  );
}
